import {
  type RelationEditor,
  useRelationEditor,
  type WithId,
} from '@framework/form/form/relations/useRelationEditor.ts';
import { message } from 'antd';
import { isEqual, maxBy } from 'lodash';
import { type Feature } from 'ol';
import type VectorSource from 'ol/source/Vector';
import { useCallback, useEffect, useRef } from 'react';
import {
  getCompetenceLayersInfo,
  type MapCadastralAreaItem,
  MapCompetenceSuffix,
  type MapIntersectionBaseItem,
  type MapIntersectionItem,
  type MapPrimaryCompetenceItem,
  type MapSecondaryCompetenceItem,
  MapSecondaryCompetencesSuffix,
} from '../../createMapSchema.ts';
import { type IMapWidget } from '../../IMapWidget.ts';
import { getIntersectionLayersInfo } from '../../layers/getIntersectionLayersInfo.ts';
import { type IMapLayer } from '../../layers/MapLayer.ts';
import { useMapCompetenceList } from '../../useMapCompetenceList.ts';
import { type IMapCompetenceIntersectionItem } from './IMapCompetenceIntersectionItem.ts';
import { useMapCalculationConfigHash } from './useMapCalculationConfigHash.ts';
import {
  buildCadastralItem,
  buildCustomIntersectionItem,
} from './utils/buildIntersectionItems.ts';
import { computeFeaturesIntersection } from './utils/computeFeaturesIntersection.ts';
import {
  computePrimaryCompetence,
  groupAndSumByCompetence,
  hasMissingCompetenceCode,
  type IRawPrimaryCompetence,
} from './utils/computePrimaryCompetence.ts';
import { createIntersectionVectorSource } from './utils/createIntersectionVectorSource.ts';
import { getCalculationArea } from './utils/getCalculationArea.ts';
import { getCompetenceNotAttributedWarning } from './utils/getCompetenceNotAttributedWarning.ts';
import { getIntersectionTruncatedError } from './utils/getIntersectionTruncatedError.ts';
import { loadSourceFeatures } from './utils/loadSourceFeatures.ts';

export interface IMapIntersectionManagerOptions {
  /** Sorgente delle feature disegnate */
  featuresSource: React.RefObject<VectorSource>;
  /** Opzioni del widget */
  widget: IMapWidget;
  /** Nome campo base del widget (per costruire i suffissi @competence, ecc.) */
  fieldName: string;
  /** Registry degli editor di intersezione, indicizzato per codice intersezione */
  intersectionEditorsRef: React.RefObject<Map<string, RelationEditor<any>>>;
  /** true se il widget è in sola lettura (disabilita fetch competenze) */
  readonly?: boolean;
}

/**
 * Scrive nell'editor solo se il nuovo valore differisce da quello corrente.
 * I calcoli automatici (intersezioni/competenze) rieseguono la scrittura anche
 * a parità di geometria (es. al refetch della lista competenze o al ricaricamento
 * delle sorgenti): senza questa guardia `replace()` marcherebbe il form come
 * modificato anche subito dopo il salvataggio.
 */
function replaceIfChanged<T>(
  editor: RelationEditor<T> | undefined,
  next: WithId<T>[],
) {
  if (!editor || isEqual(editor.getValue(), next)) return;
  editor.replace(next);
}

/**
 * Le righe inserite a mano non derivano dal calcolo e non possono essere
 * rigenerate: si conservano a ogni riscrittura dell'editor.
 */
function getManualItems(editor: RelationEditor<any> | undefined) {
  return (editor?.getValue() ?? []).filter(
    (item: MapIntersectionBaseItem) => item.isManual,
  );
}

/**
 * Si occupa di gestire il calcolo delle intersezioni tra le geometrie disegnate
 * e quelle presenti nei layer di intersezione configurati.
 *
 * Supporta N layer con intersezione abilitata. Per ogni layer:
 * - `cadastralPreset: true` → produce MapCadastralAreaItem (retrocompat @cadastral)
 * - altrimenti → produce MapIntersectionItem con attributi dinamici (@intersections_<idx>)
 *
 * Supporta N layer di competenza:
 * - layer primario → risultato scritto in `@competence` (risolto via API)
 * - layer secondari → risultati scritti in `@secondaryCompetences`
 *
 */
export function useMapIntersectionManager(
  options: IMapIntersectionManagerOptions,
) {
  const {
    featuresSource,
    widget,
    fieldName,
    intersectionEditorsRef,
    readonly,
  } = options;

  const intersectionLayers = getIntersectionLayersInfo(widget);
  const competenceLayers = getCompetenceLayersInfo(widget);

  const { isSavedHashOutdated, invalidateConfigHash, storeConfigHash } =
    useMapCalculationConfigHash({ widget, fieldName });

  /**
   * Id del giro di calcolo corrente: le risposte dei giri superati si
   * ignorano.
   */
  const calculationRunRef = useRef(0);

  const competenceQuery = useMapCompetenceList(widget, readonly);
  const competenceQueryRef = useRef(competenceQuery);
  competenceQueryRef.current = competenceQuery;

  /** Ultimo calcolo in cui tutte le sorgenti hanno risposto senza errori. */
  const sourcesCompletedRunRef = useRef<number | null>(null);

  const competenceEditor = useRelationEditor<MapPrimaryCompetenceItem>(
    `${fieldName}${MapCompetenceSuffix}`,
  );
  const competenceEditorRef = useRef(competenceEditor);
  competenceEditorRef.current = competenceEditor;

  const secondaryCompetencesEditor =
    useRelationEditor<MapSecondaryCompetenceItem>(
      `${fieldName}${MapSecondaryCompetencesSuffix}`,
    );
  const secondaryCompetencesEditorRef = useRef(secondaryCompetencesEditor);
  secondaryCompetencesEditorRef.current = secondaryCompetencesEditor;

  /** Risultato raw della competenza primaria (prima della risoluzione API). */
  const primaryResultRef = useRef<IRawPrimaryCompetence | null>(null);

  /**
   * Accumulatore dei risultati competenza secondaria.
   * Chiave: `layer.code`, valore: risultati di quel layer.
   */
  const secondaryAccumulatorRef = useRef<
    Map<string, MapSecondaryCompetenceItem[]>
  >(new Map());

  /**
   * Ricalcola le intersezioni per un singolo layer.
   */
  const computeAndStoreLayerIntersections = (
    intersectionCode: string,
    layer: IMapLayer,
    source: VectorSource,
  ) => {
    const intersection = layer.intersection;
    if (!intersection) return;

    const isCadastral = intersection.cadastralPreset === true;
    const tolerance = intersection.areaTolerance ?? 0.05;
    const maxResults = intersection.maxResults ?? 100;

    const cadastralItems: MapCadastralAreaItem[] = [];
    const customItems: MapIntersectionItem[] = [];
    const currentSource = featuresSource.current;
    if (!currentSource) return;

    computeFeaturesIntersection(
      currentSource,
      source,
      (intersectable, intersectionFeature, area, drawnFeature) => {
        // Ignoriamo le intersezioni con area minore della tolleranza
        if (area <= tolerance) return;

        if (isCadastral) {
          cadastralItems.push(
            buildCadastralItem(
              intersectable,
              intersectionFeature,
              area,
              drawnFeature,
            ),
          );
        } else {
          customItems.push(
            buildCustomIntersectionItem({
              intersectable,
              intersectionFeature,
              area,
              drawnFeature,
              attributes: intersection.attributes ?? [],
            }),
          );
        }
      },
      { bufferDistance: widget.bufferDistance },
    );

    const items = isCadastral ? cadastralItems : customItems;
    if (items.length > maxResults) {
      message.error(
        getIntersectionTruncatedError({
          found: items.length,
          max: maxResults,
        }),
      );
    }
    const truncated = items.slice(0, maxResults);

    // Le righe manuali restano in coda alle righe calcolate.
    const editor = intersectionEditorsRef.current?.get(intersectionCode);
    replaceIfChanged(editor, [...truncated, ...getManualItems(editor)]);
  };

  /**
   * Calcola e salva la competenza per un singolo layer.
   *
   * @param layer Layer di competenza
   * @param layerSource VectorSource del layer di competenza
   */
  function computeAndStoreLayerCompetence(
    layer: IMapLayer,
    layerSource: VectorSource,
  ) {
    const currentSource = featuresSource.current;
    if (!currentSource) return;
    const competence = layer.competence;
    if (!competence) return;
    const propertyName = competence.identifyingPropertyName ?? '';
    const tolerance = competence.areaTolerance ?? 0.05;
    const items: IMapCompetenceIntersectionItem[] = [];

    computeFeaturesIntersection(
      currentSource,
      layerSource,
      (intersectable, _intersectionFeature, area) => {
        if (area <= tolerance) return;
        const code = intersectable.getProperties()?.[propertyName];
        items.push({
          affectedArea: area,
          competence: code ? String(code) : null,
        });
      },
      { bufferDistance: widget.bufferDistance },
    );

    // Un poligono di competenza che interseca in modo rilevante (area sopra la
    // tolleranza configurata) ma privo di codice non produce alcuna competenza:
    // avvisiamo l'utente, altrimenti l'area verrebbe scartata silenziosamente.
    if (hasMissingCompetenceCode(items)) {
      // Dettaglio tecnico per dev/supporto; all'utente solo l'avviso neutro.
      console.warn(
        `[MapWidget] Competenza non attribuita: il layer "${layer.code}" ha uno o più poligoni intersecati senza codice (proprietà "${propertyName}" mancante o vuota).`,
      );
      message.warning(getCompetenceNotAttributedWarning());
    }

    if (competence.role === 'primary') {
      primaryResultRef.current = computePrimaryCompetence(items);
      savePrimaryCompetence();
    } else if (competence.role === 'secondary') {
      // secondaryCompetenceTypeId è obbligatorio per i layer secondari
      // (garantito da MapLayerCompetenceSchema): senza, il layer è
      // misconfigurato e non contribuisce ai risultati.
      if (!competence.secondaryCompetenceTypeId) return;
      const typeId = competence.secondaryCompetenceTypeId;
      const competenceAreas = groupAndSumByCompetence(items);
      const maxEntry = maxBy(competenceAreas, entry => entry.affectedArea);
      const results: MapSecondaryCompetenceItem[] = competenceAreas
        // Senza vectorLayerCode l'entry non identifica una competenza:
        // non inseriamo l'item.
        .filter(entry => entry.competence)
        .map(entry => ({
          vectorLayerCode: entry.competence,
          affectedArea: entry.affectedArea,
          isPrevalent: entry === maxEntry,
          secondaryCompetenceTypeId: typeId,
        }));

      secondaryAccumulatorRef.current.set(layer.code, results);
      saveSecondaryCompetences();
    }
  }

  /**
   * Svuota i risultati di competenza di un layer che non partecipa al
   * calcolo: l'hash non deve certificare righe di un calcolo vecchio.
   */
  function clearLayerCompetence(layer: IMapLayer) {
    if (layer.competence?.role === 'primary') {
      primaryResultRef.current = null;
      savePrimaryCompetence();
    } else if (layer.competence?.role === 'secondary') {
      secondaryAccumulatorRef.current.delete(layer.code);
      saveSecondaryCompetences();
    }
  }

  /**
   * Scrive l'hash quando tutte le sorgenti del calcolo corrente hanno caricato
   * senza errori e la competenza primaria non attende più l'elenco delle
   * competenze. Viene chiamata sia a fine giro sia all'arrivo dell'elenco,
   * così scrive l'ultimo dei due. Se la query dell'elenco è disattivata, la
   * primaria resta vuota e l'hash si scrive comunque.
   */
  function storeConfigHashIfReady() {
    if (sourcesCompletedRunRef.current !== calculationRunRef.current) return;
    const { list, enabled } = competenceQueryRef.current;
    const isPrimaryPending =
      !!primaryResultRef.current?.vectorLayerCode &&
      list === undefined &&
      enabled;
    if (isPrimaryPending) return;
    storeConfigHash();
  }

  /**
   * Sorgenti del giro di calcolo, con la scrittura da eseguire al caricamento.
   * Un layer con config incompleta non ha sorgente: si salta e i suoi risultati
   * calcolati si svuotano subito, prima delle richieste.
   */
  function prepareRunSources() {
    const sources: Array<{ source: VectorSource; onLoaded: () => void }> = [];
    for (const { layer, code } of intersectionLayers) {
      const source = createIntersectionVectorSource(layer);
      if (!source) {
        const editor = intersectionEditorsRef.current?.get(code);
        replaceIfChanged(editor, getManualItems(editor));
        continue;
      }
      sources.push({
        source,
        onLoaded: () => computeAndStoreLayerIntersections(code, layer, source),
      });
    }
    for (const layer of competenceLayers) {
      const source = createIntersectionVectorSource(layer);
      if (!source) {
        clearLayerCompetence(layer);
        continue;
      }
      sources.push({
        source,
        onLoaded: () => computeAndStoreLayerCompetence(layer, source),
      });
    }
    return sources;
  }

  /**
   * Un giro di calcolo: ogni tabella si scrive appena la sua sorgente
   * risponde, l'hash solo alla fine e solo se nessuna sorgente ha fallito.
   * Un giro nuovo supera quello in volo: risposte ed errori in ritardo si
   * fermano al controllo del `runId`.
   */
  async function runCalculation(feature?: Feature) {
    const currentSource = featuresSource.current;
    if (!currentSource) return;
    const { extent, projection } = getCalculationArea(currentSource, feature);
    if (!projection) return;

    const sources = prepareRunSources();
    const runId = ++calculationRunRef.current;
    // Senza sorgenti non c'è calcolo: l'hash non passa da '', che significa
    // «calcolo in corso o fallito».
    if (sources.length > 0) invalidateConfigHash();

    // Ritenta l'elenco competenze fallito, fuori dai loads: serve solo alla
    // primaria, che lo attende in storeConfigHashIfReady.
    const { isError, refetch } = competenceQueryRef.current;
    if (isError) refetch();

    const loads = sources.map(({ source, onLoaded }) =>
      loadSourceFeatures(source, extent, projection).then(() => {
        if (runId !== calculationRunRef.current) return;
        onLoaded();
      }),
    );
    completeRun(runId, await Promise.allSettled(loads));
  }

  /**
   * Chiude un giro di calcolo:
   * se è il più recente e nessuna sorgente ha fallito,
   * lo segna come completato e prova a scrivere l'hash.
   */
  function completeRun(runId: number, results: PromiseSettledResult<void>[]) {
    if (runId !== calculationRunRef.current) return;
    const failures = results.filter(result => result.status === 'rejected');
    for (const failure of failures) {
      console.warn('[MapWidget] Calcolo intersezioni fallito', failure.reason);
    }
    if (failures.length > 0) return;
    sourcesCompletedRunRef.current = runId;
    storeConfigHashIfReady();
  }

  /**
   * Trigger per il calcolo delle intersezioni. Sincrono perché passato dove
   * serve `(feature?: Feature) => void`: la Promise resta in `runCalculation`.
   * @param feature Feature aggiunta e/o modificata, necessaria per calcolare
   * correttamente l'estensione delle geometrie.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: runCalculation accede solo a ref stabili e alla config widget (intersectionLayers, competenceLayers), che non muta dopo il mount
  const triggerIntersections = useCallback((feature?: Feature) => {
    runCalculation(feature).catch(error => {
      console.error('[MapWidget] Calcolo delle intersezioni fallito', error);
    });
  }, []);

  /**
   * Ricalcola all'apertura se l'hash salvato non è quello della
   * configurazione corrente: assente (dato salvato prima dell'hash o
   * geometrie dal tag semantico), vuoto (calcolo interrotto o fallito) o
   * diverso. Da chiamare a mappa pronta; in sola lettura non fa nulla
   * perché la vista deve mostrare ciò che è stato inviato.
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: accede solo a ref e a valori stabili derivati dalla config widget
  const triggerIntersectionsIfStale = useCallback(() => {
    if (readonly) return;
    // Il controllo serve solo ai dati arrivati dal salvataggio: se un calcolo
    // è già partito, l'hash lo gestisce quel calcolo.
    if (calculationRunRef.current > 0) return;
    const currentSource = featuresSource.current;
    if (!currentSource || currentSource.getFeatures().length === 0) return;
    if (isSavedHashOutdated()) triggerIntersections();
  }, []);

  /**
   * Scrive la competenza primaria nel campo `@competence`,
   * risolvendo `vectorLayerCode` in `idCompetenza` via la lista
   * competenze dall'API (risoluzione lazy).
   */
  function savePrimaryCompetence() {
    const raw = primaryResultRef.current;
    // Senza vectorLayerCode l'intersezione non ha individuato alcuna
    // competenza: non inseriamo l'item.
    if (!raw?.vectorLayerCode) {
      replaceIfChanged(competenceEditorRef.current, []);
      return;
    }
    const { list } = competenceQueryRef.current;
    const resolved = list?.find(
      competence =>
        competence.codiceIdentificativoLayerVettoriale === raw.vectorLayerCode,
    );
    // Competenza non risolta dalla lista API: non inseriamo l'item.
    if (!resolved?.idCompetenza) {
      // Il poligono ha un codice ma nessuna Competenza configurata nel sistema
      // corrisponde a quel codiceIdentificativoLayerVettoriale: l'area non
      // produce competenza.
      // Guardia su `list`: quando è `undefined` la lista non è ancora
      // arrivata e la risoluzione è solo posticipata (verrà ritentata al
      // fetch, vedi l'effetto su `competenceQuery.list`), quindi non è un
      // errore da segnalare.
      if (list) {
        // Dettaglio tecnico (codice) per dev/supporto; all'utente solo
        // l'avviso neutro.
        console.warn(
          `[MapWidget] Competenza non attribuita: nessuna competenza configurata per il codice "${raw.vectorLayerCode}".`,
        );
        message.warning(getCompetenceNotAttributedWarning());
      }
      replaceIfChanged(competenceEditorRef.current, []);
      return;
    }
    replaceIfChanged(competenceEditorRef.current, [
      {
        vectorLayerCode: raw.vectorLayerCode,
        idCompetenza: resolved.idCompetenza,
        denominazione: resolved.denominazione ?? undefined,
        affectedArea: raw.affectedArea,
      },
    ]);
  }

  /**
   * Scrive l'aggregato delle competenze secondarie
   * nel campo `@secondaryCompetences`.
   */
  function saveSecondaryCompetences() {
    const allResults = Array.from(
      secondaryAccumulatorRef.current.values(),
    ).flat();
    replaceIfChanged(secondaryCompetencesEditorRef.current, allResults);
  }

  // Ri-flush primaria quando arriva la lista competenze dall'API
  // (risoluzione lazy: l'intersezione può scattare prima del fetch), poi
  // l'hash se il giro corrente aspettava solo la lista.
  // biome-ignore lint/correctness/useExhaustiveDependencies: flush solo al cambio lista
  useEffect(() => {
    if (!competenceQuery.list) return;
    if (primaryResultRef.current) savePrimaryCompetence();
    storeConfigHashIfReady();
  }, [competenceQuery.list]);

  /**
   * Svuota gli accumulatori dei risultati competenza.
   * Da chiamare al reset delle geometrie disegnate.
   */
  const resetCompetence = useCallback(() => {
    primaryResultRef.current = null;
    secondaryAccumulatorRef.current.clear();
    competenceEditorRef.current?.reset();
    secondaryCompetencesEditorRef.current?.reset();
  }, []);

  return {
    triggerIntersections,
    triggerIntersectionsIfStale,
    resetCompetence,
  };
}
